import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { EUnitType } from '../@types/Enums';
import PlanModel from '../models/standard/PlanModel';
import { ERegionCode } from '../utils/regionUtils';
import { useUnitContext } from './UnitProvider'; // Access Parent Context

interface IPlanContext {
  planModel: PlanModel;
  setPlanModel: Dispatch<SetStateAction<PlanModel>>;
}

const PlanContext = createContext<IPlanContext | undefined>(undefined);

const getPlanDebugSnapshot = (plan: PlanModel) => ({
  dataObjectId: plan.dataObjectId,
  eventId: plan.eventId,
  id: plan.id,
  jsonVersion: plan.jsonVersion,
  kojeId: plan.kojeId,
  kojeLevel: plan.kojeLevel,
  name: plan.name,
  region: plan.region,
  showroomId: plan.showroomId,
});

const hasPlanContextIds = (plan: PlanModel): boolean =>
  plan.id !== -1 ||
  plan.kojeId !== -1 ||
  plan.kojeLevel !== -1 ||
  plan.eventId !== -1 ||
  plan.dataObjectId !== -1 ||
  plan.showroomId !== -1;

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

interface PlanProviderProps {
  children: ReactNode;
}

export const PlanProvider: React.FC<PlanProviderProps> = ({ children }) => {
  const [planModel, setPlanModel] = useState<PlanModel>(new PlanModel(''));
  const setPlanModelWithDebug = useCallback((nextPlan: SetStateAction<PlanModel>) => {
    setPlanModel((previousPlan) => {
      const resolvedPlan = typeof nextPlan === 'function' ? (nextPlan as (previousPlan: PlanModel) => PlanModel)(previousPlan) : nextPlan;

      if (hasPlanContextIds(previousPlan) && !hasPlanContextIds(resolvedPlan)) {
        console.warn('[PlanDebug] setPlanModel is clearing active plan context', {
          next: getPlanDebugSnapshot(resolvedPlan),
          previous: getPlanDebugSnapshot(previousPlan),
          stack: new Error().stack,
        });
      }

      console.warn('[PlanDebug] setPlanModel', {
        next: getPlanDebugSnapshot(resolvedPlan),
        previous: getPlanDebugSnapshot(previousPlan),
      });

      return resolvedPlan;
    });
  }, []);
  
  // Access the Unit Context from the parent provider
  const { setUnitType } = useUnitContext();

  // --- SYNC REGION TO UNITS ---
  useEffect(() => {
    // When the plan's region changes (initial load or menu switch), set the default unit system
    if (planModel.region === ERegionCode.US) {
      setUnitType(EUnitType.IMPERIAL);
    } else {
      setUnitType(EUnitType.METRIC);
    }
  }, [planModel.region, setUnitType]);

  return (
    <PlanContext.Provider value={{ planModel, setPlanModel: setPlanModelWithDebug }}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanProvider;
